/**
 * @CODE:SPEC-ACC-001-LIST-001
 * AccommodationList Component
 *
 * Displays a grid of AccommodationCard components with pagination
 * Uses mobile-first responsive design (grid-cols-1 md:grid-cols-2 lg:grid-cols-3)
 */

import { memo } from "react";
import AccommodationCard from "./AccommodationCard";
import type { AccommodationWithDetails } from "@/features/accommodation/types";

interface AccommodationListProps {
  accommodations: AccommodationWithDetails[];
  isLoading?: boolean;
  onFavoriteChange?: (
    accommodationId: number,
    isFavorite: boolean,
  ) => Promise<void>;
}

const AccommodationList = memo(
  ({
    accommodations,
    isLoading = false,
    onFavoriteChange,
  }: AccommodationListProps) => {
    if (isLoading) {
      return (
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 12 }).map((_, idx) => (
              <div
                key={idx}
                className="bg-gray-200 rounded-lg h-96 animate-pulse"
              />
            ))}
          </div>
        </div>
      );
    }

    if (accommodations.length === 0) {
      return (
        <div className="container mx-auto px-4 py-16 text-center">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            검색 결과가 없습니다
          </h3>
          <p className="text-gray-500">
            다른 필터를 선택하여 다시 검색해보세요
          </p>
        </div>
      );
    }

    return (
      <div className="container mx-auto px-4 py-8" data-testid="accommodation-list">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {accommodations.map((accommodation, idx) => (
            <AccommodationCard
              key={accommodation.accommodation_id}
              accommodation={accommodation}
              index={idx}
              onFavoriteChange={onFavoriteChange}
            />
          ))}
        </div>
      </div>
    );
  },
);

AccommodationList.displayName = "AccommodationList";

export default AccommodationList;
